import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import Modal from './Modal';
import { Button } from '../../design-system/components/Button';
import { typography } from '../../design-system/tokens/typography';

const Message = styled.p`
  color: ${props => props.theme.colors.text};
  margin: 0;
  font-size: ${typography.fontSize.base};
  font-family: ${typography.fontFamily.primary};
  line-height: ${typography.lineHeight.relaxed};
`;

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'primary' | 'danger';
  loading?: boolean;
  /** When false, the parent closes the dialog after the async action succeeds. */
  closeOnConfirm?: boolean;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  variant = 'primary',
  loading = false,
  closeOnConfirm = true,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const resolvedConfirm = confirmText ?? t('common.confirm');
  const resolvedCancel = cancelText ?? t('common.cancel');

  const handleConfirm = () => {
    if (loading) return;
    onConfirm();
    if (closeOnConfirm) onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            {resolvedCancel}
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            loading={loading}
            disabled={loading}
            style={
              variant === 'danger'
                ? { background: theme.colors.error }
                : undefined
            }
          >
            {resolvedConfirm}
          </Button>
        </>
      }
    >
      <Message>{message}</Message>
    </Modal>
  );
};

export default ConfirmDialog;
